import { type Theme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  TextField as DsTextField,
  type TextFieldProps as DsTextFieldProps,
} from '@openx/design-system/components/TextField/TextField.tsx';
import { forwardRef, useMemo } from 'react';
import { designSystemOutlinedInput, labelTabMarginSx } from '../theme/designSystemInput.ts';
import { scopeDesignSystem } from '../theme/scopeDesignSystem.ts';
import type { TextFieldProps } from './TextField.tsx';

// `MuiInputBase` is scoped so the core theme's disabled/error input rules do not stack on top of the DS ones.
export const textFieldDesignSystemTheme = (outer: Theme): Theme => {
  const scoped = scopeDesignSystem(['MuiTextField', 'MuiInputBase'])(outer);
  return { ...scoped, components: { ...scoped.components, MuiOutlinedInput: designSystemOutlinedInput } };
};

export type DesignSystemTextFieldProps = Omit<TextFieldProps, 'designSystem'>;

// The DS layout root aligns its children to the start, so without `fullWidth` the inner FormControl keeps its
// intrinsic width; stretched, the input fills a root sized by `sx` or by the parent like the legacy field did.
const stretchInputSx = { '& > .MuiFormControl-root': { alignSelf: 'stretch' } };

// The DS TextField is outlined + medium only and takes `data-test` (it has no `dataTest`). `disableUnderline` is a
// standard-variant prop the legacy call sites pass; an OutlinedInput would forward it to the DOM. The DS TextField
// uses `required` only for its label tab, so it is also set on the native input to keep browser validation.
export const DesignSystemTextField = forwardRef<HTMLDivElement, DesignSystemTextFieldProps>(
  function DesignSystemTextField({ variant: _variant, size: _size, dataTest, sx, slotProps, ...props }, ref) {
    const inputSlot = slotProps?.input;
    const hasInputObject = typeof inputSlot === 'object';
    const { disableUnderline: _disableUnderline, ...input } = (hasInputObject ? inputSlot : {}) as {
      disableUnderline?: boolean;
    };
    const htmlInputSlot = slotProps?.htmlInput;
    const htmlInput = {
      ...(typeof htmlInputSlot === 'object' ? htmlInputSlot : undefined),
      ...(props.required && { required: true }),
    };
    // The outer theme comes from `useTheme`, which falls back to MUI's default theme when no ThemeProvider is mounted
    // (unit tests, apps without a root theme). A function `theme` prop would get no outer theme there: MUI logs an
    // error and the DS style overrides crash on the missing `theme.typography`.
    const outer = useTheme();
    const theme = useMemo(() => textFieldDesignSystemTheme(outer), [outer]);

    return (
      <ThemeProvider theme={theme}>
        <DsTextField
          ref={ref}
          {...(props as DsTextFieldProps)}
          {...(dataTest && { 'data-test': dataTest })}
          sx={[labelTabMarginSx, stretchInputSx, ...(Array.isArray(sx) ? sx : [sx])]}
          slotProps={{ ...slotProps, ...(hasInputObject && { input }), htmlInput } as DsTextFieldProps['slotProps']}
        />
      </ThemeProvider>
    );
  }
);
